import React, { useState } from 'react';
import { Button } from '../../components';
import {
    Card,
    CardBody,
    Form,
    Label,
    Input,
    Table,
    Row,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { InvoicingDto } from '../../types';
import { invoiceService } from '../../services/InvoiceService';
import { toast } from 'react-toastify';
import InvoiceEmailsTable from './InvoiceEmailsTable';
import { generateYears } from '../../common/Utils';

const SendInvoiceEmails = () => {
    const currentYear = new Date().getFullYear().toString();
    const [invoices, setInvoices] = useState<InvoicingDto[]>();
    const [fiscalYear, setFiscalYear] = useState<string>(currentYear);
    const [total, setTotal] = useState(0);
    const [paging, setPaging] = useState({ pageNumber: 1, maxPageSize: null as number | null, pageSize: 100 });
    const { control, handleSubmit } = useForm({
        defaultValues: {
            fiscalYear: currentYear
        }
    });

    const searchWithPaging = (p: typeof paging) => {
        const toastId = toast.info('Searching...', {
            autoClose: false,
        });
        invoiceService
            .getUnsentInvoices({
                institutionId: null,
                invoiceNumber: null,
                invoiceType: null,
                paymentType: null,
                year: fiscalYear,
                status: null,
                paging: p,
            })
            .then((response) => {
                setInvoices(response.invoices);
                setTotal(response.total);
                toast.update(toastId, {
                    type: 'success',
                    autoClose: 500,
                    closeOnClick: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.dismiss(toastId);
                toast.error('Failed to load invoices.');
            });
    };

    const onSubmit = () => {
        const newPaging = { ...paging, pageNumber: 1 };
        setPaging(newPaging);
        searchWithPaging(newPaging);
    };

    const reloadInvoices = () => {
        setInvoices(undefined);
        searchWithPaging(paging);
    };

    return (
        <>
            <Card>
                <CardBody>
                    <div className="d-flex align-items-center mb-4" style={{ gap: '1rem' }}>
                        <h3 className="mb-0 mt-1">Unsent Invoices</h3>
                    </div>
                    <Row>
                        <Col>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="fiscalYear" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Fiscal Year
                                    </Label>
                                    <Controller
                                        name="fiscalYear"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type="select"
                                                value={field.value ?? currentYear}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    setFiscalYear(e.target.value);
                                                }}
                                            >
                                                <option value="">Select Year</option>
                                                {generateYears().map((ct) => (
                                                    <option key={ct} value={ct}>
                                                        {ct}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    />
                                </div>
                                <div>
                                    <Button type="submit">Search</Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                </CardBody>
                {invoices && (
                    <div>
                        <div className="ml-3">
                            <h5>Total: {total}</h5>
                        </div>
                        <InvoiceEmailsTable invoices={invoices} fiscalYear={fiscalYear} total={total} onSave={() => reloadInvoices()} onClear={() => { setInvoices([]); setTotal(0); }} />
                    </div>
                )}
                {!invoices && (
                    <Table>
                        <tbody>
                            <tr><td></td></tr>
                        </tbody>
                    </Table>
                )}
                {invoices && (
                    <CardBody>
                        <Pagination size="sm" aria-label="Invoice Navigation">
                            <PaginationItem disabled={paging.pageNumber === 1}>
                                <PaginationLink
                                    previous
                                    href=""
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const newPaging = { ...paging, pageNumber: paging.pageNumber - 1 };
                                        setPaging(newPaging);
                                        searchWithPaging(newPaging);
                                    }}
                                />
                            </PaginationItem>
                            <PaginationItem>
                                <PaginationLink
                                    next
                                    href=""
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const newPaging = { ...paging, pageNumber: paging.pageNumber + 1 };
                                        setPaging(newPaging);
                                        searchWithPaging(newPaging);
                                    }}
                                />
                            </PaginationItem>
                        </Pagination>
                    </CardBody>
                )}
            </Card>
        </>
    );
};

export default SendInvoiceEmails;
